import React, { useCallback, useRef } from 'react';

import {
  preloadCreateExpenseDependencies,
  preloadCreateGroupDependencies,
} from '@/utils/preloadUtils';

import { RouteChangeLink, RouteChangeLinkProps } from './RouteChangeLink';

interface PreloadLinkProps extends RouteChangeLinkProps {
  preloadType?: 'create-group' | 'create-expense';
}

/**
 * Link component that preloads heavy dependencies on hover/focus
 */
export function PreloadLink({
  preloadType,
  children,
  onMouseEnter,
  onFocus,
  ...props
}: PreloadLinkProps) {
  const preloadStartedRef = useRef(false);
  const preloadTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const startPreload = useCallback(() => {
    if (preloadStartedRef.current || !preloadType) return;

    // Debounce preload to avoid triggering on accidental hovers
    preloadTimeoutRef.current = setTimeout(() => {
      preloadStartedRef.current = true;

      if (preloadType === 'create-group') {
        preloadCreateGroupDependencies();
      } else if (preloadType === 'create-expense') {
        preloadCreateExpenseDependencies();
      }
    }, 100); // 100ms delay to avoid accidental triggers
  }, [preloadType]);

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      startPreload();
      onMouseEnter?.(e);
    },
    [startPreload, onMouseEnter]
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLAnchorElement>) => {
      startPreload();
      onFocus?.(e);
    },
    [startPreload, onFocus]
  );

  const handleMouseLeave = useCallback(() => {
    // Cancel preload if user leaves quickly
    if (preloadTimeoutRef.current) {
      clearTimeout(preloadTimeoutRef.current);
    }
  }, []);

  return (
    <RouteChangeLink
      {...props}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
    >
      {children}
    </RouteChangeLink>
  );
}
